/**
 * Integrytas Group - Base App (MOBILE)
 * Pages: DOC.FORMAT_MASKS · DOC.PAGE_SIZES · DOC.RESOURCES — mobile-native.
 *
 * Touch-first counterpart of src/pages/doc/DocLookupPages.tsx. A DISTINCT
 * presentation (dense card list + facet chips + bottom-sheet detail + bottom-sheet
 * editor) over the SAME data/logic layer as the desktop maintenance screens:
 *   - reads via apiDb (doc_format_mask / doc_page_size / doc_resource, each with
 *     its `_tl` translation join resolved against the active locale — §17.B4,
 *     never a raw code/id on screen),
 *   - every write (create, field edit, activate/deactivate) routes through the
 *     Common Routine (routedInsert / routedUpdate) — §2 single write path.
 * It never re-hosts the desktop grid; §16. The three list surfaces share one
 * generic shell (LookupListShell); each entity supplies its own load + editor.
 *
 * Scope note (mirrors MobileUserEditSheet): the mobile editor authors the
 * active-locale display name; full multi-locale translation stays on the desktop
 * TranslationsEditor. Other locales' `_tl` rows are left untouched by a save.
 */

import { useEffect, useMemo, useState } from 'react';
import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Search, Inbox, Power, X, Plus, Hash, FileText, Image as ImageIcon } from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import { apiDb } from '../../lib/api_client';
import { routedInsert, routedUpdate } from '../../lib/routed_writes';
import { useActiveLocale } from '../../lib/use_active_locale';
import { MobilePageHeader, MobileStatStrip, MobileContentCard, MobileCardRow, MobileDetailRow, MobileFilterChips, MobileStatusPill } from '../components';
import type { AuthSession } from '../../types/auth';
import { hasAccess } from '../../types/auth';
import type { MobilePageComponentProps } from '../mobile_page_components';

/* ── Shared types & helpers ──────────────────────────────────────────────── */

interface TlRow { locale_code: string; display_text: string }

/** CHECK-enum option: stored value stays the code; only the label is localized. */
interface EnumOpt { value: string; labelKey: string; fallback: string }

const MASK_KINDS: EnumOpt[] = [
  { value: 'NUMBER', labelKey: 'doc.mask_kind.NUMBER', fallback: 'Number' },
  { value: 'CURRENCY', labelKey: 'doc.mask_kind.CURRENCY', fallback: 'Currency' },
  { value: 'PERCENT', labelKey: 'doc.mask_kind.PERCENT', fallback: 'Percent' },
  { value: 'DATE', labelKey: 'doc.mask_kind.DATE', fallback: 'Date' },
  { value: 'PHONE', labelKey: 'doc.mask_kind.PHONE', fallback: 'Phone' },
  { value: 'TEXT', labelKey: 'doc.mask_kind.TEXT', fallback: 'Text' },
];

const RESOURCE_KINDS: EnumOpt[] = [
  { value: 'IMAGE', labelKey: 'doc.resource_kind.IMAGE', fallback: 'Image' },
  { value: 'TEXT_SNIPPET', labelKey: 'doc.resource_kind.TEXT_SNIPPET', fallback: 'Text Snippet' },
];

/** Resolve a `_tl` list to the active-locale display_text, EN_US then code fallback. */
function tlName(tls: TlRow[], locale: string, code: string): string {
  return (
    tls.find((x) => x.locale_code === locale)?.display_text ??
    tls.find((x) => x.locale_code === 'EN_US')?.display_text ??
    tls[0]?.display_text ??
    code
  );
}

/** Curry a code→localized-label resolver for a CHECK-enum. */
function makeEnumLabel(opts: EnumOpt[], t: (k: string, d: string) => string): (v: string) => string {
  return (v: string) => {
    const o = opts.find((x) => x.value === v);
    return o ? t(o.labelKey, o.fallback) : v;
  };
}

type StatusFacet = 'all' | 'active' | 'inactive';

/* ══════════════════════════════════════════════════════════════════════════ */
/*  Generic list shell (search + facet chips + card list + detail sheet)        */
/* ══════════════════════════════════════════════════════════════════════════ */

interface EditSheetProps<T> {
  session: AuthSession;
  open: boolean;
  row: T | null;
  onClose: () => void;
  onSaved: () => void;
}

type SheetMode<T> = { kind: 'detail'; row: T } | { kind: 'edit'; row: T } | { kind: 'create' } | null;

interface LookupShellProps<T> {
  session: AuthSession;
  pageCode: string;   // the RBAC page this lookup maps to (§6)
  icon: LucideIcon;
  table: string;
  pkColumn: string;
  title: string;
  description: string;
  searchLabel: string;
  emptyLabel: string;
  newLabel: string;
  detailLabel: string;
  /** Optional single-select kind facet (rendered as a second chip row). */
  kindOptions?: EnumOpt[];
  load: (locale: string) => Promise<T[]>;
  getPk: (row: T) => string;
  getKind?: (row: T) => string;
  getActive: (row: T) => 'Y' | 'N';
  getDisplay: (row: T) => string;
  getSubtitle: (row: T) => string;
  getDetailFields: (row: T) => { k: string; v: ReactNode }[];
  renderEdit: (props: EditSheetProps<T>) => ReactNode;
}

function LookupListShell<T>(props: LookupShellProps<T>) {
  const {
    session, pageCode, icon: Icon, table, pkColumn, title, description, searchLabel, emptyLabel, newLabel, detailLabel,
    kindOptions, load, getPk, getKind, getActive, getDisplay, getSubtitle, getDetailFields, renderEdit,
  } = props;
  // §6 RBAC: one shell serves several lookup pages, so the verbs resolve
  // against THIS page's code from props, not a file constant.
  const canAdd    = hasAccess(session, pageCode, 'add');
  const canChange = hasAccess(session, pageCode, 'change');
  const { t } = useTranslation();
  const activeLocale = useActiveLocale();
  const [rows, setRows] = useState<T[]>([]);
  const [filter, setFilter] = useState('');
  const [statusFacet, setStatusFacet] = useState<StatusFacet>('active');
  const [kindFacet, setKindFacet] = useState<string>('all');
  const [mode, setMode] = useState<SheetMode<T>>(null);
  const [busy, setBusy] = useState(false);
  const [actionBusy, setActionBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const reload = async () => {
    setBusy(true);
    setError(null);
    try {
      setRows(await load(activeLocale));
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(false);
    }
  };

  // Re-run on mount and whenever the active locale changes (names follow it).
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { void reload(); }, [activeLocale]);

  const filtered = useMemo(() => {
    const q = filter.trim().toLowerCase();
    return rows.filter((r) => {
      const act = getActive(r);
      if (statusFacet === 'active' && act !== 'Y') return false;
      if (statusFacet === 'inactive' && act !== 'N') return false;
      if (kindOptions && kindFacet !== 'all' && getKind && getKind(r) !== kindFacet) return false;
      if (!q) return true;
      return getPk(r).toLowerCase().includes(q) || getDisplay(r).toLowerCase().includes(q);
    });
  }, [rows, filter, statusFacet, kindFacet, kindOptions, getActive, getKind, getPk, getDisplay]);

  const statusFacets: { key: StatusFacet; label: string }[] = [
    { key: 'all', label: t('mobile.doc.all', 'All') },
    { key: 'active', label: t('common.boolean.active', 'Active') },
    { key: 'inactive', label: t('common.boolean.inactive', 'Inactive') },
  ];

  const kindLabel = kindOptions ? makeEnumLabel(kindOptions, t) : undefined;

  const detailRow = mode && mode.kind === 'detail' ? mode.row : null;
  const editOpen = mode !== null && (mode.kind === 'edit' || mode.kind === 'create');
  const editRow = mode && mode.kind === 'edit' ? mode.row : null;

  const toggleActive = async (row: T) => {
    setActionBusy(true);
    setError(null);
    const next = getActive(row) === 'Y' ? 'N' : 'Y';
    // §2: route through the Common Routine (forensic stamp + audit + hooks).
    const { error: e } = await routedUpdate(session, table, pkColumn, getPk(row), { is_active: next });
    setActionBusy(false);
    if (e) { setError(e.message); return; }
    setMode(null);
    void reload();
  };

  return (
    <section className="m-screen">
      <MobilePageHeader title={title} description={description} />

      <MobileStatStrip
        stats={[
          { value: rows.length, label: t('mobile.doc.stat.total', 'Total') },
          { value: rows.filter((r) => getActive(r) === 'Y').length, label: t('mobile.doc.stat.active', 'Active') },
        ]}
      />

      <div className="m-searchbox">
        <Search />
        <input
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
          placeholder={searchLabel}
          aria-label={searchLabel}
        />
      </div>

      <MobileFilterChips
        chips={statusFacets.map((f) => ({ value: f.key, label: f.label }))}
        isActive={(v) => statusFacet === v}
        onSelect={(v) => setStatusFacet(v as StatusFacet)}
      />

      {kindOptions && kindLabel && (
        <MobileFilterChips
          chips={[
            { value: 'all', label: t('mobile.doc.all_kinds', 'All kinds') },
            ...kindOptions.map((o) => ({ value: o.value, label: kindLabel(o.value) })),
          ]}
          isActive={(v) => kindFacet === v}
          onSelect={(v) => setKindFacet(v)}
        />
      )}

      <button
        type="button"
        className="m-btn m-btn-primary"
        style={{ margin: '6px 0 4px' }}
        onClick={canAdd ? (() => setMode({ kind: 'create' })) : undefined}
      >
        <Plus size={16} /> {newLabel}
      </button>

      {error && (
        <MobileContentCard>
          <div className="m-empty" style={{ color: 'var(--color-danger)' }}>{error}</div>
        </MobileContentCard>
      )}

      {filtered.length === 0 && !busy ? (
        <MobileContentCard><div className="m-empty"><Inbox /> {emptyLabel}</div></MobileContentCard>
      ) : (
        <MobileContentCard list>
          {filtered.map((r) => (
            <MobileCardRow
              key={getPk(r)}
              lead={<Icon />}
              leadTone={getActive(r) === 'Y' ? 'blue' : 'slate'}
              title={getDisplay(r)}
              subtitle={getSubtitle(r)}
              trailing={
                <MobileStatusPill tone={getActive(r) === 'Y' ? 'ok' : 'no'}>
                  {getActive(r) === 'Y' ? t('common.boolean.active', 'Active') : t('common.boolean.inactive', 'Inactive')}
                </MobileStatusPill>
              }
              chevron
              onClick={() => setMode({ kind: 'detail', row: r })}
            />
          ))}
        </MobileContentCard>
      )}

      {/* Detail bottom-sheet */}
      <div className={'m-scrim' + (detailRow ? ' m-open' : '')} onClick={() => { if (!actionBusy) setMode(null); }} />
      <div className={'m-sheet' + (detailRow ? ' m-open' : '')} aria-label={detailLabel}>
        <div className="m-grabber" />
        {detailRow && (
          <>
            <div className="m-sheet-id">
              <span className="m-av"><Icon size={20} /></span>
              <span>
                <div className="m-nm">{getDisplay(detailRow)}</div>
                <div className="m-em">{getPk(detailRow)}</div>
              </span>
            </div>
            <MobileContentCard>
              <MobileDetailRow
                label={t('mobile.doc.status', 'Status')}
                value={
                  <MobileStatusPill tone={getActive(detailRow) === 'Y' ? 'ok' : 'no'}>
                    {getActive(detailRow) === 'Y' ? t('common.boolean.active', 'Active') : t('common.boolean.inactive', 'Inactive')}
                  </MobileStatusPill>
                }
              />
              {getDetailFields(detailRow).map((f) => (
                <MobileDetailRow key={f.k} label={f.k} value={f.v} />
              ))}
            </MobileContentCard>
            <div className="m-actionbar">
              <button
                type="button"
                className="m-btn m-btn-primary"
                disabled={actionBusy}
                onClick={canChange ? (() => setMode({ kind: 'edit', row: detailRow })) : undefined}
              >
                <FileText size={16} /> {t('mobile.doc.edit', 'Edit')}
              </button>
              <button
                type="button"
                className={'m-btn ' + (getActive(detailRow) === 'Y' ? 'm-btn-danger' : 'm-btn-ghost')}
                disabled={actionBusy}
                onClick={canChange ? (() => void toggleActive(detailRow)) : undefined}
              >
                <Power size={16} />
                {getActive(detailRow) === 'Y' ? t('users.list.actions.deactivate', 'Deactivate') : t('users.list.actions.activate', 'Activate')}
              </button>
              <button type="button" className="m-btn m-btn-ghost" disabled={actionBusy} onClick={() => setMode(null)} aria-label={t('mobile.close', 'Close')}>
                <X size={16} />
              </button>
            </div>
          </>
        )}
      </div>

      {/* Edit / create bottom-sheet (entity-specific) */}
      {renderEdit({
        session,
        open: editOpen,
        row: editRow,
        onClose: () => setMode(null),
        onSaved: () => { setMode(null); void reload(); },
      })}
    </section>
  );
}

/* ── Shared edit-sheet frame ─────────────────────────────────────────────── */

interface EditFrameProps {
  open: boolean;
  icon: LucideIcon;
  title: string;
  subtitle: string;
  busy: boolean;
  error: string | null;
  onClose: () => void;
  onSave: () => void;
  children: ReactNode;
}

function EditSheetFrame({ open, icon: Icon, title, subtitle, busy, error, onClose, onSave, children }: EditFrameProps) {
  const { t } = useTranslation();
  return (
    <>
      <div className={'m-scrim' + (open ? ' m-open' : '')} onClick={() => { if (!busy) onClose(); }} />
      <div className={'m-sheet' + (open ? ' m-open' : '')} aria-label={title}>
        <div className="m-grabber" />
        {open && (
          <>
            <div className="m-sheet-id">
              <span className="m-av"><Icon size={20} /></span>
              <span>
                <div className="m-nm">{title}</div>
                <div className="m-em">{subtitle}</div>
              </span>
            </div>
            <div className="m-fieldcard">{children}</div>
            {error && <div className="m-card"><div className="m-empty" style={{ color: 'var(--color-danger)' }}>{error}</div></div>}
            <div className="m-actionbar">
              <button type="button" className="m-btn m-btn-primary" disabled={busy} onClick={onSave}>
                {busy ? t('common.saving', 'Saving…') : t('common.save', 'Save changes')}
              </button>
              <button type="button" className="m-btn m-btn-ghost" disabled={busy} onClick={onClose} aria-label={t('common.cancel', 'Cancel')}>
                <X size={16} />
              </button>
            </div>
          </>
        )}
      </div>
    </>
  );
}

/** Insert/replace the active-locale `_tl` name row (leaves other locales intact). */
async function saveTlName(
  session: AuthSession, tlTable: string, codeColumn: string, code: string, locale: string, name: string,
): Promise<{ error: { message: string } | null }> {
  const { error } = await routedInsert(session, tlTable, {
    [codeColumn]: code, locale_code: locale, display_text: name,
  });
  return { error };
}

/* ══════════════════════════════════════════════════════════════════════════ */
/*  FORMAT MASKS — doc_format_mask                                              */
/* ══════════════════════════════════════════════════════════════════════════ */

interface FormatMaskRow {
  format_mask_code: string;
  mask_kind: string;
  is_active: 'Y' | 'N';
  sort_order: number;
  display_text: string;
  tl: TlRow[];
}

async function loadFormatMasks(locale: string): Promise<FormatMaskRow[]> {
  const { data, error } = await apiDb
    .from('doc_format_mask')
    .select('format_mask_code, mask_kind, is_active, sort_order, doc_format_mask_tl ( locale_code, display_text )')
    .order('sort_order');
  if (error) throw error;
  type Raw = Omit<FormatMaskRow, 'display_text' | 'tl'> & { doc_format_mask_tl?: TlRow[] };
  return ((data ?? []) as unknown as Raw[]).map((r) => {
    const tl = r.doc_format_mask_tl ?? [];
    return {
      format_mask_code: r.format_mask_code,
      mask_kind: r.mask_kind,
      is_active: r.is_active,
      sort_order: r.sort_order,
      tl,
      display_text: tlName(tl, locale, r.format_mask_code),
    };
  });
}

function FormatMaskEditSheet({ session, open, row, onClose, onSaved }: EditSheetProps<FormatMaskRow>) {
  const { t } = useTranslation();
  const activeLocale = useActiveLocale();
  const isCreate = row === null;
  const [code, setCode] = useState('');
  const [kind, setKind] = useState('NUMBER');
  const [name, setName] = useState('');
  const [sortOrder, setSortOrder] = useState('0');
  const [active, setActive] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    setCode(row?.format_mask_code ?? '');
    setKind(row?.mask_kind ?? 'NUMBER');
    setName(row?.display_text ?? '');
    setSortOrder(String(row?.sort_order ?? 0));
    setActive(!row || row.is_active === 'Y');
    setError(null);
  }, [open, row]);

  const save = async () => {
    if (isCreate && !code.trim()) { setError(t('mobile.doc.err.code_required', 'Code is required.')); return; }
    if (!name.trim()) { setError(t('mobile.doc.err.name_required', 'Name is required.')); return; }
    setBusy(true);
    setError(null);
    const pk = isCreate ? code.trim() : row.format_mask_code;
    // §2: every write via the Common Routine.
    const patch = { mask_kind: kind, sort_order: Number(sortOrder), is_active: active ? 'Y' : 'N' };
    const { error: e } = isCreate
      ? await routedInsert(session, 'doc_format_mask', { format_mask_code: pk, ...patch })
      : await routedUpdate(session, 'doc_format_mask', 'format_mask_code', pk, patch);
    if (e) { setBusy(false); setError(e.message); return; }
    const { error: te } = await saveTlName(session, 'doc_format_mask_tl', 'format_mask_code', pk, activeLocale, name.trim());
    setBusy(false);
    if (te) { setError(te.message); return; }
    onSaved();
  };

  return (
    <EditSheetFrame
      open={open}
      icon={Hash}
      title={isCreate ? t('mobile.doc.masks.new', 'New format mask') : t('mobile.doc.edit', 'Edit')}
      subtitle={isCreate ? t('mobile.doc.masks.title', 'Format Masks') : (row?.format_mask_code ?? '')}
      busy={busy}
      error={error}
      onClose={onClose}
      onSave={() => void save()}
    >
      {isCreate && (
        <div className="m-formrow">
          <label htmlFor="m-fm-code">{t('mobile.doc.code', 'Code')}</label>
          <input id="m-fm-code" className="m-inp" value={code} onChange={(e) => setCode(e.target.value)} placeholder="CURRENCY_USD" autoFocus />
        </div>
      )}
      <div className="m-formrow">
        <label htmlFor="m-fm-kind">{t('mobile.doc.masks.kind', 'Kind')}</label>
        <select id="m-fm-kind" className="m-sel" value={kind} onChange={(e) => setKind(e.target.value)}>
          {MASK_KINDS.map((k) => <option key={k.value} value={k.value}>{t(k.labelKey, k.fallback)}</option>)}
        </select>
      </div>
      <div className="m-formrow">
        <label htmlFor="m-fm-name">{t('mobile.doc.name', 'Name')}</label>
        <input id="m-fm-name" className="m-inp" value={name} onChange={(e) => setName(e.target.value)} />
      </div>
      <div className="m-formrow">
        <label htmlFor="m-fm-sort">{t('mobile.doc.sort_order', 'Sort order')}</label>
        <input id="m-fm-sort" className="m-inp" type="number" value={sortOrder} onChange={(e) => setSortOrder(e.target.value)} />
      </div>
      <div className="m-formrow m-formrow-inline">
        <span>
          <div className="m-formrow-l">{t('common.boolean.active', 'Active')}</div>
          <div className="m-formrow-s">{t('mobile.doc.active_hint', 'Available for selection')}</div>
        </span>
        <button
          type="button"
          role="switch"
          aria-checked={active}
          className={'m-switch ' + (active ? 'm-on' : 'm-off')}
          onClick={() => setActive((v) => !v)}
        >
          <span className="m-knob" />
        </button>
      </div>
    </EditSheetFrame>
  );
}

export function MobileDocFormatMaskPage({ session }: MobilePageComponentProps) {
  const { t } = useTranslation();
  const kindLabel = makeEnumLabel(MASK_KINDS, t);
  return (
    <LookupListShell<FormatMaskRow>
      pageCode="DOC.FORMAT_MASKS"
      session={session}
      icon={Hash}
      table="doc_format_mask"
      pkColumn="format_mask_code"
      title={t('mobile.doc.masks.title', 'Format Masks')}
      description={t('mobile.doc.masks.description', 'Reusable number, date and text formats for generated documents.')}
      searchLabel={t('mobile.doc.masks.search', 'Search format masks')}
      emptyLabel={t('mobile.doc.masks.none', 'No format masks match')}
      newLabel={t('mobile.doc.masks.new', 'New format mask')}
      detailLabel={t('mobile.doc.masks.detail', 'Format mask detail')}
      kindOptions={MASK_KINDS}
      load={loadFormatMasks}
      getPk={(r) => r.format_mask_code}
      getKind={(r) => r.mask_kind}
      getActive={(r) => r.is_active}
      getDisplay={(r) => r.display_text}
      getSubtitle={(r) => `${r.format_mask_code} · ${kindLabel(r.mask_kind)}`}
      getDetailFields={(r) => [
        { k: t('mobile.doc.masks.kind', 'Kind'), v: kindLabel(r.mask_kind) },
        { k: t('mobile.doc.sort_order', 'Sort order'), v: r.sort_order },
      ]}
      renderEdit={(p) => <FormatMaskEditSheet {...p} />}
    />
  );
}

/* ══════════════════════════════════════════════════════════════════════════ */
/*  PAGE SIZES — doc_page_size                                                  */
/* ══════════════════════════════════════════════════════════════════════════ */

interface PageSizeRow {
  page_size_code: string;
  width_pt: number;
  height_pt: number;
  is_active: 'Y' | 'N';
  sort_order: number;
  display_text: string;
  tl: TlRow[];
}

async function loadPageSizes(locale: string): Promise<PageSizeRow[]> {
  const { data, error } = await apiDb
    .from('doc_page_size')
    .select('page_size_code, width_pt, height_pt, is_active, sort_order, doc_page_size_tl ( locale_code, display_text )')
    .order('sort_order');
  if (error) throw error;
  type Raw = Omit<PageSizeRow, 'display_text' | 'tl'> & { doc_page_size_tl?: TlRow[] };
  return ((data ?? []) as unknown as Raw[]).map((r) => {
    const tl = r.doc_page_size_tl ?? [];
    return {
      page_size_code: r.page_size_code,
      width_pt: r.width_pt,
      height_pt: r.height_pt,
      is_active: r.is_active,
      sort_order: r.sort_order,
      tl,
      display_text: tlName(tl, locale, r.page_size_code),
    };
  });
}

function PageSizeEditSheet({ session, open, row, onClose, onSaved }: EditSheetProps<PageSizeRow>) {
  const { t } = useTranslation();
  const activeLocale = useActiveLocale();
  const isCreate = row === null;
  const [code, setCode] = useState('');
  const [width, setWidth] = useState('');
  const [height, setHeight] = useState('');
  const [name, setName] = useState('');
  const [sortOrder, setSortOrder] = useState('0');
  const [active, setActive] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    setCode(row?.page_size_code ?? '');
    setWidth(row ? String(row.width_pt) : '');
    setHeight(row ? String(row.height_pt) : '');
    setName(row?.display_text ?? '');
    setSortOrder(String(row?.sort_order ?? 0));
    setActive(!row || row.is_active === 'Y');
    setError(null);
  }, [open, row]);

  const save = async () => {
    if (isCreate && !code.trim()) { setError(t('mobile.doc.err.code_required', 'Code is required.')); return; }
    if (!width.trim() || !height.trim()) { setError(t('mobile.doc.sizes.err.dimensions', 'Width and height (pt) are required.')); return; }
    if (!name.trim()) { setError(t('mobile.doc.err.name_required', 'Name is required.')); return; }
    setBusy(true);
    setError(null);
    const pk = isCreate ? code.trim() : row.page_size_code;
    const patch = { width_pt: Number(width), height_pt: Number(height), sort_order: Number(sortOrder), is_active: active ? 'Y' : 'N' };
    const { error: e } = isCreate
      ? await routedInsert(session, 'doc_page_size', { page_size_code: pk, ...patch })
      : await routedUpdate(session, 'doc_page_size', 'page_size_code', pk, patch);
    if (e) { setBusy(false); setError(e.message); return; }
    const { error: te } = await saveTlName(session, 'doc_page_size_tl', 'page_size_code', pk, activeLocale, name.trim());
    setBusy(false);
    if (te) { setError(te.message); return; }
    onSaved();
  };

  return (
    <EditSheetFrame
      open={open}
      icon={FileText}
      title={isCreate ? t('mobile.doc.sizes.new', 'New page size') : t('mobile.doc.edit', 'Edit')}
      subtitle={isCreate ? t('mobile.doc.sizes.title', 'Page Sizes') : (row?.page_size_code ?? '')}
      busy={busy}
      error={error}
      onClose={onClose}
      onSave={() => void save()}
    >
      {isCreate && (
        <div className="m-formrow">
          <label htmlFor="m-ps-code">{t('mobile.doc.code', 'Code')}</label>
          <input id="m-ps-code" className="m-inp" value={code} onChange={(e) => setCode(e.target.value)} placeholder="A4" autoFocus />
        </div>
      )}
      <div className="m-formrow">
        <label htmlFor="m-ps-width">{t('mobile.doc.sizes.width', 'Width (pt)')}</label>
        <input id="m-ps-width" className="m-inp" type="number" value={width} onChange={(e) => setWidth(e.target.value)} placeholder="595.28" />
      </div>
      <div className="m-formrow">
        <label htmlFor="m-ps-height">{t('mobile.doc.sizes.height', 'Height (pt)')}</label>
        <input id="m-ps-height" className="m-inp" type="number" value={height} onChange={(e) => setHeight(e.target.value)} placeholder="841.89" />
      </div>
      <div className="m-formrow">
        <label htmlFor="m-ps-name">{t('mobile.doc.name', 'Name')}</label>
        <input id="m-ps-name" className="m-inp" value={name} onChange={(e) => setName(e.target.value)} />
      </div>
      <div className="m-formrow">
        <label htmlFor="m-ps-sort">{t('mobile.doc.sort_order', 'Sort order')}</label>
        <input id="m-ps-sort" className="m-inp" type="number" value={sortOrder} onChange={(e) => setSortOrder(e.target.value)} />
      </div>
      <div className="m-formrow m-formrow-inline">
        <span>
          <div className="m-formrow-l">{t('common.boolean.active', 'Active')}</div>
          <div className="m-formrow-s">{t('mobile.doc.active_hint', 'Available for selection')}</div>
        </span>
        <button
          type="button"
          role="switch"
          aria-checked={active}
          className={'m-switch ' + (active ? 'm-on' : 'm-off')}
          onClick={() => setActive((v) => !v)}
        >
          <span className="m-knob" />
        </button>
      </div>
    </EditSheetFrame>
  );
}

export function MobileDocPageSizePage({ session }: MobilePageComponentProps) {
  const { t } = useTranslation();
  return (
    <LookupListShell<PageSizeRow>
      pageCode="DOC.PAGE_SIZES"
      session={session}
      icon={FileText}
      table="doc_page_size"
      pkColumn="page_size_code"
      title={t('mobile.doc.sizes.title', 'Page Sizes')}
      description={t('mobile.doc.sizes.description', 'Named page dimensions available when laying out documents.')}
      searchLabel={t('mobile.doc.sizes.search', 'Search page sizes')}
      emptyLabel={t('mobile.doc.sizes.none', 'No page sizes match')}
      newLabel={t('mobile.doc.sizes.new', 'New page size')}
      detailLabel={t('mobile.doc.sizes.detail', 'Page size detail')}
      load={loadPageSizes}
      getPk={(r) => r.page_size_code}
      getActive={(r) => r.is_active}
      getDisplay={(r) => r.display_text}
      getSubtitle={(r) => `${r.page_size_code} · ${r.width_pt} × ${r.height_pt} pt`}
      getDetailFields={(r) => [
        { k: t('mobile.doc.sizes.width', 'Width (pt)'), v: r.width_pt },
        { k: t('mobile.doc.sizes.height', 'Height (pt)'), v: r.height_pt },
        { k: t('mobile.doc.sort_order', 'Sort order'), v: r.sort_order },
      ]}
      renderEdit={(p) => <PageSizeEditSheet {...p} />}
    />
  );
}

/* ══════════════════════════════════════════════════════════════════════════ */
/*  STATIC RESOURCES — doc_resource                                             */
/* ══════════════════════════════════════════════════════════════════════════ */

interface ResourceRow {
  resource_code: string;
  resource_kind: string;
  text_content: string | null;
  is_active: 'Y' | 'N';
  sort_order: number;
  display_text: string;
  tl: TlRow[];
}

async function loadResources(locale: string): Promise<ResourceRow[]> {
  const { data, error } = await apiDb
    .from('doc_resource')
    .select('resource_code, resource_kind, text_content, is_active, sort_order, doc_resource_tl ( locale_code, display_text )')
    .order('sort_order');
  if (error) throw error;
  type Raw = Omit<ResourceRow, 'display_text' | 'tl'> & { doc_resource_tl?: TlRow[] };
  return ((data ?? []) as unknown as Raw[]).map((r) => {
    const tl = r.doc_resource_tl ?? [];
    return {
      resource_code: r.resource_code,
      resource_kind: r.resource_kind,
      text_content: r.text_content,
      is_active: r.is_active,
      sort_order: r.sort_order,
      tl,
      display_text: tlName(tl, locale, r.resource_code),
    };
  });
}

function ResourceEditSheet({ session, open, row, onClose, onSaved }: EditSheetProps<ResourceRow>) {
  const { t } = useTranslation();
  const activeLocale = useActiveLocale();
  const isCreate = row === null;
  const [code, setCode] = useState('');
  const [kind, setKind] = useState('TEXT_SNIPPET');
  const [textContent, setTextContent] = useState('');
  const [name, setName] = useState('');
  const [sortOrder, setSortOrder] = useState('0');
  const [active, setActive] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    setCode(row?.resource_code ?? '');
    setKind(row?.resource_kind ?? 'TEXT_SNIPPET');
    setTextContent(row?.text_content ?? '');
    setName(row?.display_text ?? '');
    setSortOrder(String(row?.sort_order ?? 0));
    setActive(!row || row.is_active === 'Y');
    setError(null);
  }, [open, row]);

  // The kind is fixed on the row once created; only new rows choose it.
  const effectiveKind = isCreate ? kind : (row?.resource_kind ?? kind);
  const isImage = effectiveKind === 'IMAGE';

  const save = async () => {
    if (isCreate && !code.trim()) { setError(t('mobile.doc.err.code_required', 'Code is required.')); return; }
    if (!name.trim()) { setError(t('mobile.doc.err.name_required', 'Name is required.')); return; }
    // IMAGE payloads attach a DMS document through the designer/import path; this
    // maintenance surface only authors TEXT_SNIPPET inline content (mirrors desktop).
    if (isCreate && effectiveKind !== 'TEXT_SNIPPET') {
      setError(t('mobile.doc.resources.err.image_via_designer', 'Image resources are added through the designer or an import.'));
      return;
    }
    if (effectiveKind === 'TEXT_SNIPPET' && !textContent.trim()) {
      setError(t('mobile.doc.resources.err.text_required', 'Text content is required for a text snippet.'));
      return;
    }
    setBusy(true);
    setError(null);
    const pk = isCreate ? code.trim() : row.resource_code;
    let e: { message: string } | null;
    if (isCreate) {
      ({ error: e } = await routedInsert(session, 'doc_resource', {
        resource_code: pk, resource_kind: effectiveKind, text_content: textContent, sort_order: Number(sortOrder), is_active: active ? 'Y' : 'N',
      }));
    } else {
      const patch: Record<string, unknown> = { sort_order: Number(sortOrder), is_active: active ? 'Y' : 'N' };
      if (effectiveKind === 'TEXT_SNIPPET') patch.text_content = textContent;
      ({ error: e } = await routedUpdate(session, 'doc_resource', 'resource_code', pk, patch));
    }
    if (e) { setBusy(false); setError(e.message); return; }
    const { error: te } = await saveTlName(session, 'doc_resource_tl', 'resource_code', pk, activeLocale, name.trim());
    setBusy(false);
    if (te) { setError(te.message); return; }
    onSaved();
  };

  return (
    <EditSheetFrame
      open={open}
      icon={ImageIcon}
      title={isCreate ? t('mobile.doc.resources.new', 'New resource') : t('mobile.doc.edit', 'Edit')}
      subtitle={isCreate ? t('mobile.doc.resources.title', 'Static Resources') : (row?.resource_code ?? '')}
      busy={busy}
      error={error}
      onClose={onClose}
      onSave={() => void save()}
    >
      {isCreate && (
        <div className="m-formrow">
          <label htmlFor="m-rs-code">{t('mobile.doc.code', 'Code')}</label>
          <input id="m-rs-code" className="m-inp" value={code} onChange={(e) => setCode(e.target.value)} placeholder="ADDRESS_FOOTER" autoFocus />
        </div>
      )}
      <div className="m-formrow">
        <label htmlFor="m-rs-kind">{t('mobile.doc.resources.kind', 'Kind')}</label>
        <select id="m-rs-kind" className="m-sel" value={effectiveKind} onChange={(e) => setKind(e.target.value)} disabled={!isCreate}>
          {RESOURCE_KINDS.map((k) => <option key={k.value} value={k.value}>{t(k.labelKey, k.fallback)}</option>)}
        </select>
      </div>
      <div className="m-formrow">
        <label htmlFor="m-rs-name">{t('mobile.doc.name', 'Name')}</label>
        <input id="m-rs-name" className="m-inp" value={name} onChange={(e) => setName(e.target.value)} />
      </div>
      {isImage ? (
        <div className="m-formrow">
          <label>{t('mobile.doc.resources.content', 'Content')}</label>
          <div className="m-formrow-ro">{t('mobile.doc.resources.image_hint', 'Image resources reference a DMS document, attached through the designer or an import.')}</div>
        </div>
      ) : (
        <div className="m-formrow">
          <label htmlFor="m-rs-text">{t('mobile.doc.resources.text_content', 'Text content')}</label>
          <textarea id="m-rs-text" className="m-inp" rows={4} value={textContent} onChange={(e) => setTextContent(e.target.value)} />
        </div>
      )}
      <div className="m-formrow">
        <label htmlFor="m-rs-sort">{t('mobile.doc.sort_order', 'Sort order')}</label>
        <input id="m-rs-sort" className="m-inp" type="number" value={sortOrder} onChange={(e) => setSortOrder(e.target.value)} />
      </div>
      <div className="m-formrow m-formrow-inline">
        <span>
          <div className="m-formrow-l">{t('common.boolean.active', 'Active')}</div>
          <div className="m-formrow-s">{t('mobile.doc.active_hint', 'Available for selection')}</div>
        </span>
        <button
          type="button"
          role="switch"
          aria-checked={active}
          className={'m-switch ' + (active ? 'm-on' : 'm-off')}
          onClick={() => setActive((v) => !v)}
        >
          <span className="m-knob" />
        </button>
      </div>
    </EditSheetFrame>
  );
}

export function MobileDocResourcePage({ session }: MobilePageComponentProps) {
  const { t } = useTranslation();
  const kindLabel = makeEnumLabel(RESOURCE_KINDS, t);
  return (
    <LookupListShell<ResourceRow>
      pageCode="DOC.RESOURCES"
      session={session}
      icon={ImageIcon}
      table="doc_resource"
      pkColumn="resource_code"
      title={t('mobile.doc.resources.title', 'Static Resources')}
      description={t('mobile.doc.resources.description', 'Shared images and text snippets reused across document templates.')}
      searchLabel={t('mobile.doc.resources.search', 'Search resources')}
      emptyLabel={t('mobile.doc.resources.none', 'No resources match')}
      newLabel={t('mobile.doc.resources.new', 'New resource')}
      detailLabel={t('mobile.doc.resources.detail', 'Resource detail')}
      kindOptions={RESOURCE_KINDS}
      load={loadResources}
      getPk={(r) => r.resource_code}
      getKind={(r) => r.resource_kind}
      getActive={(r) => r.is_active}
      getDisplay={(r) => r.display_text}
      getSubtitle={(r) => `${r.resource_code} · ${kindLabel(r.resource_kind)}`}
      getDetailFields={(r) => [
        { k: t('mobile.doc.resources.kind', 'Kind'), v: kindLabel(r.resource_kind) },
        { k: t('mobile.doc.sort_order', 'Sort order'), v: r.sort_order },
      ]}
      renderEdit={(p) => <ResourceEditSheet {...p} />}
    />
  );
}
